import React, { useState } from 'react';
import { X, Hash, Package, Truck, Delete, Clock, AlertTriangle } from 'lucide-react';

const DELIVERY_TIME_WINDOWS = {
  1: '1:00 PM - 4:00 PM',
  2: '2:00 PM - 5:00 PM',
  3: '3:00 PM - 6:00 PM',
  4: '4:00 PM - 7:00 PM',
  5: '5:00 PM - 8:00 PM',
  6: '6:00 PM - 9:00 PM',
};

function getDeliveryTimeWindow(slot) {
  return DELIVERY_TIME_WINDOWS[Number(slot)] || null;
}

function SequenceModal({ point, onClose, onSubmit }) {
  const [sequence, setSequence] = useState(point.sequence?.toString() || '');
  const [error, setError] = useState('');

  function handleNumberClick(num) {
    // Limit to 3 digits (max 999)
    if (sequence.length < 3) {
      const newSequence = sequence + num;
      setSequence(newSequence);
      setError('');
    }
  }

  function handleDelete() {
    setSequence(sequence.slice(0, -1));
    setError('');
  }

  function handleClear() {
    setSequence('');
    setError('');
  }

  function handleSubmit() {
    const sequenceNum = parseInt(sequence, 10);
    
    if (!sequence || isNaN(sequenceNum) || sequenceNum < 1) {
      setError('Por favor ingresa un número válido mayor a 0');
      return;
    }

    if (sequenceNum > 999) {
      setError('El número debe ser menor a 1000');
      return;
    }

    onSubmit(sequenceNum);
  }

  const isPickup = point.type === 'pickup';
  const deliveryTimeWindow = !isPickup ? getDeliveryTimeWindow(point.slot) : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-3 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {isPickup ? (
              <Package className="w-5 h-5 text-[#67A43C]" />
            ) : (
              <Truck className="w-5 h-5 text-[#B1336E]" />
            )}
            <h2 className={`text-lg font-extrabold font-montserrat ${isPickup ? 'text-[#67A43C]' : 'text-[#B1336E]'}`}>
              Asignar Secuencia
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Point Info */}
          <div className={`rounded-lg p-3 ${isPickup ? 'bg-[#67A43C]/10' : 'bg-[#B1336E]/10'}`}>
            <p className="text-sm font-montserrat text-slate-700">
              <span className="font-semibold">
                {isPickup ? 'Recojo' : 'Entrega'}:
              </span>{' '}
              {isPickup 
                ? (point.customer_name || 'N/A')
                : (point.recipient_name || 'N/A')
              }
            </p>
            {point.address && (
              <p className="text-xs font-montserrat text-slate-600 mt-1">
                📍 {point.address.length > 40 ? point.address.substring(0, 40) + '...' : point.address}
              </p>
            )}
          </div>

          {!isPickup && point.user_linked_to_provider === true && (
            <div className="rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-900 font-montserrat">
              Entrega de tienda vinculada a proveedor. Considera priorizarla al asignar la secuencia.
            </div>
          )}

          {deliveryTimeWindow && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="flex items-center gap-1.5 text-red-700">
                    <Clock className="w-3.5 h-3.5" />
                    <p className="text-xs font-extrabold font-montserrat">
                      Ventana horaria: {deliveryTimeWindow}
                    </p>
                  </div>
                  <p className="text-xs text-red-700 font-montserrat mt-1">
                    Asigna esta entrega en una posicion que permita llegar dentro de su horario.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Digital Display */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 font-montserrat mb-2">
              Número de Secuencia
            </label>
            <div className={`relative rounded-xl p-6 border-4 ${
              isPickup 
                ? 'border-[#67A43C]/30 bg-[#67A43C]/5' 
                : 'border-[#B1336E]/30 bg-[#B1336E]/5'
            }`}>
              <div className="flex items-center justify-center gap-2">
                <Hash className={`w-6 h-6 ${isPickup ? 'text-[#67A43C]' : 'text-[#B1336E]'}`} />
                <div className="flex-1 text-center">
                  <div className={`text-5xl font-bold font-montserrat ${
                    sequence 
                      ? (isPickup ? 'text-[#67A43C]' : 'text-[#B1336E]')
                      : 'text-slate-300'
                  }`}>
                    {sequence || '---'}
                  </div>
                </div>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-xs text-red-600 font-montserrat text-center">
                {error}
              </p>
            )}
          </div>

          {/* Number Pad */}
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumberClick(num.toString())}
                disabled={sequence.length >= 3}
                className={`py-4 rounded-xl font-bold text-xl font-montserrat transition-all ${
                  sequence.length >= 3
                    ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                    : isPickup
                    ? 'bg-[#67A43C]/10 text-[#67A43C] hover:bg-[#67A43C]/20 active:scale-95'
                    : 'bg-[#B1336E]/10 text-[#B1336E] hover:bg-[#B1336E]/20 active:scale-95'
                }`}
              >
                {num}
              </button>
            ))}
            
            {/* Clear Button */}
            <button
              type="button"
              onClick={handleClear}
              disabled={!sequence}
              className={`py-4 rounded-xl font-semibold text-sm font-montserrat transition-all ${
                !sequence
                  ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  : 'bg-red-50 text-red-600 hover:bg-red-100 active:scale-95'
              }`}
            >
              Limpiar
            </button>
            
            {/* Zero Button */}
            <button
              type="button"
              onClick={() => handleNumberClick('0')}
              disabled={sequence.length >= 3 || !sequence}
              className={`py-4 rounded-xl font-bold text-xl font-montserrat transition-all ${
                sequence.length >= 3 || !sequence
                  ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  : isPickup
                  ? 'bg-[#67A43C]/10 text-[#67A43C] hover:bg-[#67A43C]/20 active:scale-95'
                  : 'bg-[#B1336E]/10 text-[#B1336E] hover:bg-[#B1336E]/20 active:scale-95'
              }`}
            >
              0
            </button>
            
            {/* Delete Button */}
            <button
              type="button"
              onClick={handleDelete}
              disabled={!sequence}
              className={`py-4 rounded-xl font-semibold text-sm font-montserrat transition-all flex items-center justify-center gap-1 ${
                !sequence
                  ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              <Delete className="w-4 h-4" />
              <span className="text-xs">Borrar</span>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all active:scale-95 font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!sequence}
              className={`flex-1 px-4 py-3 font-semibold rounded-xl transition-all active:scale-95 font-montserrat ${
                !sequence
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : isPickup
                  ? 'bg-[#67A43C] hover:bg-[#67A43C]/90 text-white shadow-md'
                  : 'bg-[#B1336E] hover:bg-[#B1336E]/90 text-white shadow-md'
              }`}
            >
              Asignar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SequenceModal;
